import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LuFileText } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';

const SLUG = 'tax-filing';
const FORM_TYPES = ['1040', '1120-S', '1065', '1120'];

const parsePriceInput = (raw) => {
  if (raw === '' || raw == null) return 0;
  const parsed = parseFloat(String(raw));
  return Number.isFinite(parsed) ? parsed : 0;
};

const preventNumberWheel = (e) => e.currentTarget.blur();

const emptySection = (order) => ({
  sectionTitle: '',
  sectionOrder: order,
  isActive: true,
  questions: [],
});

const emptyQuestion = (order) => ({
  text: '',
  order,
  isRequired: false,
  helpText: '',
  isActive: true,
});

const defaultForm = () => ({
  isPublished: true,
  pageIcon: 'LuFileText',
  instructionBanner: '',
  tourButtonLabel: '',
  formBadgePrefix: 'Form ',
  lockedBannerTitle: '',
  lockedBannerText: '',
  lockedPayButtonLabel: '',
  wipBannerTitle: '',
  wipBannerText: '',
  wipPayButtonLabel: '',
  historyTitle: '',
  summaryTableTitle: '',
  memberSummaryTableTitle: '',
  stepCards: [
    { step: 1, title: '', instruction: '', progressMessage: '' },
    { step: 2, title: '', instruction: '', progressMessage: '' },
    { step: 3, title: '', instruction: '', progressMessage: '', clientPendingInstruction: '', clientPendingProgressMessage: '' },
    { step: 4, title: '', instruction: '', progressMessage: '', clientPendingInstruction: '', clientPendingProgressMessage: '' },
  ],
  forms: Object.fromEntries(
    FORM_TYPES.map((formType) => [formType, { formType, label: `Form ${formType}`, isActive: true, sections: [] }])
  ),
});

const TaxFilingPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('page');
  const [activeForm, setActiveForm] = useState('1040');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        const mergedForms = { ...defaultForm().forms };
        FORM_TYPES.forEach((formType) => {
          mergedForms[formType] = {
            ...mergedForms[formType],
            ...(c.forms?.[formType] || {}),
            sections: (c.forms?.[formType]?.sections || []).map((section, i) => ({
              ...section,
              sectionOrder: section.sectionOrder ?? i,
              questions: (section.questions || []).map((question, qi) => ({ ...question, order: question.order ?? qi })),
            })),
          };
        });
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          stepCards: defaultForm().stepCards.map((card, index) => ({ ...card, ...(c.stepCards?.[index] || {}) })),
          forms: mergedForms,
        });
      }
    } catch {
      toastError('Failed to load tax filing page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateStepCard = (index, field, value) =>
    setForm((prev) => {
      const stepCards = [...prev.stepCards];
      stepCards[index] = { ...stepCards[index], [field]: value };
      return { ...prev, stepCards };
    });

  const updateFormMeta = (formType, field, value) =>
    setForm((prev) => ({
      ...prev,
      forms: { ...prev.forms, [formType]: { ...prev.forms[formType], [field]: value } },
    }));

  const updateSection = (formType, sectionIndex, field, value) =>
    setForm((prev) => {
      const sections = [...(prev.forms[formType]?.sections || [])];
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value };
      return { ...prev, forms: { ...prev.forms, [formType]: { ...prev.forms[formType], sections } } };
    });

  const updateQuestion = (formType, sectionIndex, questionIndex, field, value) =>
    setForm((prev) => {
      const sections = [...(prev.forms[formType]?.sections || [])];
      const questions = [...(sections[sectionIndex]?.questions || [])];
      questions[questionIndex] = { ...questions[questionIndex], [field]: value };
      sections[sectionIndex] = { ...sections[sectionIndex], questions };
      return { ...prev, forms: { ...prev.forms, [formType]: { ...prev.forms[formType], sections } } };
    });

  const addSection = (formType) =>
    setForm((prev) => {
      const sections = [...(prev.forms[formType]?.sections || [])];
      sections.push(emptySection(sections.length));
      return { ...prev, forms: { ...prev.forms, [formType]: { ...prev.forms[formType], sections } } };
    });

  const removeSection = (formType, sectionIndex) =>
    setForm((prev) => {
      const sections = (prev.forms[formType]?.sections || []).filter((_, i) => i !== sectionIndex);
      return { ...prev, forms: { ...prev.forms, [formType]: { ...prev.forms[formType], sections } } };
    });

  const moveSection = (formType, sectionIndex, dir) =>
    setForm((prev) => {
      const sections = [...(prev.forms[formType]?.sections || [])];
      const next = sectionIndex + dir;
      if (next < 0 || next >= sections.length) return prev;
      [sections[sectionIndex], sections[next]] = [sections[next], sections[sectionIndex]];
      return {
        ...prev,
        forms: {
          ...prev.forms,
          [formType]: {
            ...prev.forms[formType],
            sections: sections.map((section, i) => ({ ...section, sectionOrder: i })),
          },
        },
      };
    });

  const addQuestion = (formType, sectionIndex) =>
    setForm((prev) => {
      const sections = [...(prev.forms[formType]?.sections || [])];
      const questions = [...(sections[sectionIndex]?.questions || [])];
      questions.push(emptyQuestion(questions.length));
      sections[sectionIndex] = { ...sections[sectionIndex], questions };
      return { ...prev, forms: { ...prev.forms, [formType]: { ...prev.forms[formType], sections } } };
    });

  const removeQuestion = (formType, sectionIndex, questionIndex) =>
    setForm((prev) => {
      const sections = [...(prev.forms[formType]?.sections || [])];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        questions: sections[sectionIndex].questions.filter((_, i) => i !== questionIndex),
      };
      return { ...prev, forms: { ...prev.forms, [formType]: { ...prev.forms[formType], sections } } };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...form,
        forms: Object.fromEntries(
          FORM_TYPES.map((formType) => [
            formType,
            {
              ...form.forms[formType],
              sections: (form.forms[formType]?.sections || []).map((section, sectionIndex) => ({
                ...section,
                sectionOrder: sectionIndex,
                questions: (section.questions || []).map((question, questionIndex) => ({
                  ...question,
                  order: questionIndex,
                })),
              })),
            },
          ])
        ),
      };
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: payload, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Tax filing page saved and checklist synced');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded tax filing defaults? This also resets checklist questions in the database.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const tabs = [
    { id: 'page', label: 'Page & Banners' },
    { id: 'steps', label: 'Step Cards' },
    ...FORM_TYPES.map((formType) => ({ id: formType, label: formType })),
  ];

  const currentForm = form.forms[activeForm] || { sections: [] };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={6} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Tax Filing Service"
        description="Edit step cards, banners, and form-wise checklist questions for the user Tax Filing page."
        icon={LuFileText}
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => navigate('/portal-cms')} className="px-4 py-2 rounded-xl border text-sm font-medium">
              Back
            </button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm font-medium flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> {resetting ? 'Resetting...' : 'Reset defaults'}
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save & sync checklist'}
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveTab(tab.id);
              if (FORM_TYPES.includes(tab.id)) setActiveForm(tab.id);
            }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>

        {activeTab === 'page' && (
          <>
            <h3 className="font-bold">Instruction banner & labels</h3>
            <textarea
              className="input-modern min-h-[80px]"
              value={form.instructionBanner}
              onChange={(e) => updateField('instructionBanner', e.target.value)}
              placeholder="Scrolling instruction banner text"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ['tourButtonLabel', 'Tour button label'],
                ['formBadgePrefix', 'Form badge prefix'],
                ['historyTitle', 'History section title'],
                ['summaryTableTitle', 'Primary summary table title'],
                ['memberSummaryTableTitle', 'Member summary title ({name})'],
                ['lockedBannerTitle', 'Locked banner title'],
                ['lockedBannerText', 'Locked banner text'],
                ['lockedPayButtonLabel', 'Locked pay button'],
                ['wipBannerTitle', 'WIP banner title'],
                ['wipBannerText', 'WIP banner text'],
                ['wipPayButtonLabel', 'WIP pay button'],
              ].map(([key, placeholder]) => (
                <input key={key} className="input-modern" value={form[key] || ''} onChange={(e) => updateField(key, e.target.value)} placeholder={placeholder} />
              ))}
            </div>
          </>
        )}

        {activeTab === 'steps' && (
          <>
            <h3 className="font-bold">Workflow step cards</h3>
            {form.stepCards.map((card, index) => (
              <div key={card.step} className="rounded-xl border p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/30">
                <h4 className="font-semibold">Step {card.step}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input className="input-modern" value={card.title || ''} onChange={(e) => updateStepCard(index, 'title', e.target.value)} placeholder="Step title" />
                  <input className="input-modern" value={card.progressMessage || ''} onChange={(e) => updateStepCard(index, 'progressMessage', e.target.value)} placeholder="Progress message" />
                  <textarea className="input-modern md:col-span-2 min-h-[72px]" value={card.instruction || ''} onChange={(e) => updateStepCard(index, 'instruction', e.target.value)} placeholder="Step instruction tooltip" />
                  {(card.step === 3 || card.step === 4) && (
                    <>
                      <input className="input-modern" value={card.clientPendingProgressMessage || ''} onChange={(e) => updateStepCard(index, 'clientPendingProgressMessage', e.target.value)} placeholder="Client pending progress message" />
                      <textarea className="input-modern min-h-[72px]" value={card.clientPendingInstruction || ''} onChange={(e) => updateStepCard(index, 'clientPendingInstruction', e.target.value)} placeholder="Client pending instruction" />
                    </>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {FORM_TYPES.includes(activeTab) && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-bold">Form {activeForm} checklist</h3>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={currentForm.isActive !== false} onChange={(e) => updateFormMeta(activeForm, 'isActive', e.target.checked)} />
                Active form
              </label>
            </div>
            <input
              className="input-modern"
              value={currentForm.label || ''}
              onChange={(e) => updateFormMeta(activeForm, 'label', e.target.value)}
              placeholder="Form badge label (e.g. Form 1040)"
            />

            <div className="flex items-center justify-between gap-3 pt-2">
              <h4 className="text-sm font-semibold uppercase text-gray-500">Sections (form-wise groups)</h4>
              <button type="button" onClick={() => addSection(activeForm)} className="text-sm text-primary-600 flex items-center gap-1">
                <FaPlus /> Add section
              </button>
            </div>

            {(currentForm.sections || []).map((section, sectionIndex) => (
              <div key={`${activeForm}-section-${sectionIndex}`} className="rounded-2xl border p-4 space-y-3 bg-primary-50/20 dark:bg-primary-900/10">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase text-primary-600">Section {sectionIndex + 1}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveSection(activeForm, sectionIndex, -1)} disabled={sectionIndex === 0} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveSection(activeForm, sectionIndex, 1)} disabled={sectionIndex === currentForm.sections.length - 1} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
                    <button type="button" onClick={() => removeSection(activeForm, sectionIndex)} className="p-2 rounded-lg text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <input
                  className="input-modern"
                  value={section.sectionTitle || ''}
                  onChange={(e) => updateSection(activeForm, sectionIndex, 'sectionTitle', e.target.value)}
                  placeholder="Section title (shown as form group header to user)"
                />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={section.isActive !== false} onChange={(e) => updateSection(activeForm, sectionIndex, 'isActive', e.target.checked)} />
                  Active section
                </label>

                <div className="space-y-3 pl-2 border-l-2 border-primary-200 dark:border-primary-800">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase text-gray-500">Questions</span>
                    <button type="button" onClick={() => addQuestion(activeForm, sectionIndex)} className="text-xs text-primary-600 flex items-center gap-1">
                      <FaPlus /> Add question
                    </button>
                  </div>
                  {(section.questions || []).map((question, questionIndex) => (
                    <div key={`${activeForm}-q-${sectionIndex}-${questionIndex}`} className="rounded-xl border p-3 space-y-2 bg-white/70 dark:bg-dark-900/40">
                      <textarea
                        className="input-modern min-h-[64px]"
                        value={question.text || ''}
                        onChange={(e) => updateQuestion(activeForm, sectionIndex, questionIndex, 'text', e.target.value)}
                        placeholder="Question text"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-[120px,1fr,auto] gap-2 items-start">
                        <input
                          className="input-modern"
                          type="number"
                          step="1"
                          value={question.order ?? questionIndex}
                          onChange={(e) => updateQuestion(activeForm, sectionIndex, questionIndex, 'order', parsePriceInput(e.target.value))}
                          onWheel={preventNumberWheel}
                          placeholder="Order"
                        />
                        <input
                          className="input-modern"
                          value={question.helpText || ''}
                          onChange={(e) => updateQuestion(activeForm, sectionIndex, questionIndex, 'helpText', e.target.value)}
                          placeholder="Help text (optional)"
                        />
                        <button type="button" onClick={() => removeQuestion(activeForm, sectionIndex, questionIndex)} className="p-2 text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!question.isRequired} onChange={(e) => updateQuestion(activeForm, sectionIndex, questionIndex, 'isRequired', e.target.checked)} />
                        Required
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={question.isActive !== false} onChange={(e) => updateQuestion(activeForm, sectionIndex, questionIndex, 'isActive', e.target.checked)} />
                        Active
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default TaxFilingPortalCMS;
